import { useState, useEffect, useRef, useCallback } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import {
  get,
  set,
  initialise,
  doesPathAcceptsBanner,
  validate,
  isWindowHidden,
  saveAdParams,
  expireAdParams,
  getDefaultPreferences,
  getOpenAppPreferences,
  getDiscardPreferences,
  getOpenAppSuccessPreferences,
  buildAndroidIntentUri,
  TIMEOUT_OPEN_IN_APP,
  OPEN_IN_APP_DEFAULT_ACTION,
  OPEN_IN_APP_USER_ACTION,
  OPEN_IN_APP_UNEXPECTED_USER_ACTION,
  NATIVE_BANNER_STORAGE_KEY
} from "@src/template/helpers/nativeAppBanner";
import { isAndroid, isIos } from "@src/helpers/deviceDetection";
import styles from "./index.css";
import deferredStyles from "./deferred.css";
import { withTranslation } from "@template/components/translation";
import { trackNativeAppBannerGetApp } from "@src/helpers/eventing/events";
import { isWindowDefined } from "@src/helpers/url";
import { addPageAction } from "@src/helpers/monitoring";
import Icon from "@template/components/Icon";
import NameRating from "../AppDownload/NameRating";
import Button from "../Button";
import AppLogo from "../AppDownload/AppLogo";
import { useGetAppDownloadUrl } from "@template/utils/hooks/useGetAppDownloadUrl";
import { handleDownloadApp } from "@template/helpers/handleDownloadApp";
import useNativeAppBannerCampaign from "./useNativeAppBannerCampaign";

let timeout;

const CAMPAIGN_MESSAGE = "nativeappbanner_campaign_text";

const useHideBannerCallback = isBannerDisplayed => {
  const callbackRef = useRef(null);
  const setCallback = cb => {
    callbackRef.current = cb;
  };

  useEffect(() => {
    if (!isBannerDisplayed && callbackRef.current) {
      callbackRef.current();
    }
    callbackRef.current = null;
  }, [isBannerDisplayed]);

  return setCallback;
};

const resetBannerOnTimeout = (expireOpenInApp, showBannerFn) => {
  return () => {
    const settings = getDefaultPreferences();
    const isHidden = isWindowHidden();
    if (isHidden && expireOpenInApp < Date.now()) {
      set(NATIVE_BANNER_STORAGE_KEY, settings);
      clearTimeout(timeout);
    } else {
      showBannerFn(settings);
      set(NATIVE_BANNER_STORAGE_KEY, settings);
    }
  };
};

const NativeAppBanner = ({
  welcomeMessage,
  storeCode,
  formatTranslation,
  defaultStoreUrl
}) => {
  const { appDownloadUrl, osPlatform, isLoading } = useGetAppDownloadUrl({
    defaultStoreUrl
  });
  const isSupportedPlatform = !!osPlatform;

  const [isBannerDisplayed, setIsBannerDisplayed] = useState(false);
  const { theme, showCampaignText, isCampaignReady, trackClick } =
    useNativeAppBannerCampaign({
      isBannerDisplayed,
      osPlatform
    });

  const [nativeBannerSettings, setNativeBannerSettings] = useState(
    getDefaultPreferences()
  );

  const browserVisibilityChangeAndroid = useCallback(() => {
    if (!isSupportedPlatform || !isWindowHidden()) {
      return;
    }
    const preferences = get(NATIVE_BANNER_STORAGE_KEY);
    if (isWindowHidden() && preferences?.expireOpenInApp >= Date.now()) {
      clearTimeout(timeout);
      const prefUpdate = getOpenAppSuccessPreferences();

      set(NATIVE_BANNER_STORAGE_KEY, prefUpdate);
      setNativeBannerSettings(prefUpdate);
    }
  }, [isSupportedPlatform]);

  const showBanner = useCallback(
    withSettings => {
      if (!isBannerDisplayed) {
        addPageAction("nativeAppBannerShow");
      }
      setIsBannerDisplayed(true);
      if (withSettings !== null) {
        setNativeBannerSettings(withSettings);
      }
    },
    [isBannerDisplayed]
  );

  const handleMobileAppUrlsAndroid = useCallback(() => {
    const preferences = get(NATIVE_BANNER_STORAGE_KEY);

    if (
      preferences === null ||
      preferences?.openInApp === OPEN_IN_APP_DEFAULT_ACTION
    ) {
      return;
    }

    const { expireOpenInApp, openInApp } = preferences;

    if (
      expireOpenInApp >= Date.now() &&
      openInApp === OPEN_IN_APP_UNEXPECTED_USER_ACTION
    ) {
      clearTimeout(timeout);
      set(NATIVE_BANNER_STORAGE_KEY, getDefaultPreferences());
    }

    if (openInApp === OPEN_IN_APP_USER_ACTION) {
      const resetAction = resetBannerOnTimeout(expireOpenInApp, showBanner);

      timeout = setTimeout(resetAction, TIMEOUT_OPEN_IN_APP);
      set(
        NATIVE_BANNER_STORAGE_KEY,
        Object.assign({}, preferences, {
          openInApp: OPEN_IN_APP_UNEXPECTED_USER_ACTION
        })
      );
    }
  }, [showBanner]);

  useEffect(() => {
    const preferences = get(NATIVE_BANNER_STORAGE_KEY);
    if (preferences !== null) {
      setNativeBannerSettings(preferences);
    }
    if (isWindowDefined() && isAndroid()) {
      window.addEventListener("beforeunload", handleMobileAppUrlsAndroid);
      window.addEventListener(
        "visibilitychange",
        browserVisibilityChangeAndroid
      );
      return () => {
        window.removeEventListener("beforeunload", handleMobileAppUrlsAndroid);
        window.removeEventListener(
          "visibilitychange",
          browserVisibilityChangeAndroid
        );
      };
    }
  }, [browserVisibilityChangeAndroid, handleMobileAppUrlsAndroid]);

  useEffect(() => {
    expireAdParams();
    saveAdParams();
  }, []);

  useEffect(() => {
    if (
      validate({ isSupportedPlatform, url: appDownloadUrl, welcomeMessage })
    ) {
      initialise(
        showBanner,
        nativeBannerSettings,
        appDownloadUrl,
        doesPathAcceptsBanner(storeCode)
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [welcomeMessage, storeCode, isSupportedPlatform, appDownloadUrl]);

  const discardBanner = () => {
    trackClick("close");
    const discardPreferences = getDiscardPreferences();
    hideBanner(discardPreferences, () => {
      set(NATIVE_BANNER_STORAGE_KEY, discardPreferences);
    });
  };

  const openInAppCallback = () => {
    if (!appDownloadUrl || isLoading) return;
    trackClick("viewInApp");
    const openAppPreferences = getOpenAppPreferences();
    hideBanner(openAppPreferences, () => {
      if (isIos()) {
        set(NATIVE_BANNER_STORAGE_KEY, getOpenAppSuccessPreferences());
      } else {
        set(NATIVE_BANNER_STORAGE_KEY, openAppPreferences);
      }
      try {
        const urlToNavigate = isAndroid()
          ? buildAndroidIntentUri(appDownloadUrl)
          : appDownloadUrl;
        window.location.assign(urlToNavigate);
      } catch (error) {
        console.warn(
          "Failed to navigate to app URL, opening in new tab",
          error
        );
        window.open(appDownloadUrl, "_blank");
      }
    });
  };

  const handleGetApp = () => {
    if (!appDownloadUrl || isLoading) return;
    handleDownloadApp({
      appDownloadUrl,
      trackingCallback: () => {
        trackNativeAppBannerGetApp(osPlatform);
        trackClick("getApp");
      }
    });
  };

  const hideBannerCallback = useHideBannerCallback(isBannerDisplayed);

  const hideBanner = (withSettings, fn) => {
    setIsBannerDisplayed(false);
    setNativeBannerSettings(withSettings);
    hideBannerCallback(fn);
  };

  const appStoreName = isIos() ? "App" : "Play";
  const appMessage = (
    <span className={showCampaignText ? styles.headline : styles.sbAppMessage}>
      {formatTranslation(
        showCampaignText
          ? CAMPAIGN_MESSAGE
          : "nativeappbanner_the_asos_appstore_text",
        { store_market: appStoreName }
      )}
    </span>
  );

  return (
    <div
      className={cx(
        styles.defaultVisibilityBanner,
        styles.asosAppSmartBanner,
        theme?.className
      )}
      style={
        isBannerDisplayed && isCampaignReady
          ? { display: "block" }
          : { display: "none" }
      }
    >
      <div className={styles.sbMessageView}>
        <div
          className={cx(styles.sbShowRatings, styles.sbMessage, {
            [styles.headlineRow]: showCampaignText
          })}
        >
          <AppLogo />
          <div
            className={cx(styles.sbAppRatings, {
              [styles.headlineRatings]: showCampaignText
            })}
          >
            {showCampaignText && appMessage}
            <NameRating isNativeBanner={true} showName={!showCampaignText} />
            {!showCampaignText && appMessage}
          </div>
        </div>
        <div className={styles.sbActions}>
          <Button
            data-testid="native-app-banner-open"
            theme="secondary"
            onClick={openInAppCallback}
            classes={cx(styles.sbBtns, {
              [styles.outlineButton]: theme?.outlineViewInApp
            })}
          >
            {formatTranslation("nativeappbanner_open_in_the_app_text")}
          </Button>
          <Button
            onClick={handleGetApp}
            theme={theme?.getAppTheme ?? "green"}
            classes={cx(styles.sbInstall, styles.sbBtns)}
          >
            {formatTranslation("nativeappbanner_install_text")}
          </Button>
        </div>
      </div>
      <button
        className={cx(styles.sbClose, {
          [styles.campaignClose]: theme
        })}
        aria-label={formatTranslation("icon_close")}
        onClick={discardBanner}
      >
        <Icon icon={deferredStyles.sbCloseIcon} />
      </button>
    </div>
  );
};

NativeAppBanner.propTypes = {
  formatTranslation: PropTypes.func.isRequired,
  storeCode: PropTypes.string.isRequired,
  welcomeMessage: PropTypes.shape({
    browsingLocalStore: PropTypes.bool
  }),
  defaultStoreUrl: PropTypes.string.isRequired
};

export default withTranslation(NativeAppBanner);
